import { CommonModule } from '@angular/common';
import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../shared/components/card/card/card';
import { Navbar } from '../../../../shared/components/navbar/navbar';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractService } from '../../../../services/contract.service';
import { finalize } from 'rxjs';
import { MessageService } from 'primeng/api';
import type { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ContractPaymentFrequency, ContractPaymentType, ContractRenewType, ContractStatus, ContractTypeValue } from '../../../../interfaces/contract';
import { SelectModule } from 'primeng/select';
import { ContractStatusPipe } from "../../../../shared/pipes/contract-status-pipe";
import dayjs from 'dayjs';
import { MultiSelect } from 'primeng/multiselect';
import { TextareaModule } from 'primeng/textarea';
import { ContractRenewTypePipe } from "../../../../shared/pipes/contract-renew-type-pipe";
import { ContractTypeValuePipe } from "../../../../shared/pipes/contract-type-value-pipe";
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ContractPaymentTypePipe } from "../../../../shared/pipes/contract-payment-type-pipe";
import { ContractPaymentFrequencyPipe } from "../../../../shared/pipes/contract-payment-frequency-pipe";
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-contract',
  imports: [
    CommonModule,
    Navbar,
    Card,
    CardHeader,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    DatePickerModule,
    SelectModule,
    ContractStatusPipe,
    MultiSelect,
    TextareaModule,
    MessageModule,
    SkeletonModule,
    ContractRenewTypePipe,
    ContractTypeValuePipe,
    ContractPaymentTypePipe,
    ContractPaymentFrequencyPipe
],
  templateUrl: './contract.html',
  styleUrl: './contract.css',
})
export class Contract {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _contractService: ContractService = inject(ContractService);

  protected contractStatus: typeof ContractStatus = ContractStatus;
  protected contractRenewType: typeof ContractRenewType = ContractRenewType;
  protected contractTypeValue: typeof ContractTypeValue = ContractTypeValue;
  protected contractPaymentType: typeof ContractPaymentType = ContractPaymentType;
  protected contractPaymentFrequency: typeof ContractPaymentFrequency = ContractPaymentFrequency;

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public title: WritableSignal<string> = signal<string>('Contrato');

  public contractForm: FormGroup = this._fb.group({
    id: [null],
    title: [null],
    start_date: [null],
    status: [null],
    signature_date: [null],
    execution_date: [null],
    termination_date: [null],
    people: [[]],
    description: [null],
    termination_for_cause_days: [null],
    termination_for_convenience_days: [null],
    renew_type: [null],
    renewal_notice_period_days: [null],
    due_date: [null],
    type_value: [null],
    value: [null],
    payment_type: [null],
    payment_first_installment: [null],
    payment_installment: [null],
    payment_frequency: [null],
    payment_day: [null],
    payment_start_date: [null],
    payment_end_date: [null],
    docs: [[]],
    notes: [[]]
  });

  get contractId(): string {
    return this.contractForm.get('id')?.value;
  }

  public ngOnInit(): void {
    const { id } = this._activatedRoute.snapshot.params;
    this._getContract(id);
  }

  private _toogleLoading(): void {
    this.loading.update(value => !value);
  }

  private _getContract(id: string): void {
    this._toogleLoading();

    this._contractService.show(id)
      .pipe(finalize(() => this._toogleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.title.set(response.data.title);
            this.contractForm.patchValue({
              ...response.data,
              people_id: response.data.people?.map((person: any) => person.id),
              start_date: response.data.start_date ? dayjs(response.data.start_date).toDate() : null,
              due_date: response.data.due_date ? dayjs(response.data.due_date).toDate() : null,
              signature_date: response.data.signature_date ? dayjs(response.data.signature_date).toDate() : null,
              execution_date: response.data.execution_date ? dayjs(response.data.execution_date).toDate() : null,
              termination_date: response.data.termination_date ? dayjs(response.data.termination_date).toDate() : null,
              payment_start_date: response.data.payment_start_date ? dayjs(response.data.payment_start_date).toDate() : null,
              payment_end_date: response.data.payment_end_date ? dayjs(response.data.payment_end_date).toDate() : null,
            });

            this.contractForm.disable();
          }
        },
        error: (error: HttpErrorResponse) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao consultar contrato',
            detail: error.error.message,
            life: 3000
          });

          this._router.navigate(['contracts']);
        },
      })
  }

  public openFile(url: string): void {
    window.open(url, '_blank');
  }

  public back(): void {
    this._router.navigate(['contracts']);
  }

}
